import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { mutate } from "swr";

import { useAuth } from "@/contexts/auth-context";
import { useCompany } from "@/contexts/company-context";
import { isCompanyWorkspaceCacheKey } from "@/lib/context-cache-keys";
import { hasFeature } from "@/lib/types/workspace";
import type { Workspace, WorkspaceContextResponse, WorkspaceRole } from "@/lib/types/workspace";
import { useWorkspaceApi } from "@/hooks/use-workspace-api";

interface WorkspaceContextType {
  currentWorkspace: WorkspaceContextResponse | null;
  selectedWorkspace: Workspace | null;
  availableWorkspaces: Workspace[];
  loading: boolean;
  workspacesLoading: boolean;
  workspacesError: Error | null;
  workspaceContextError: Error | null;
  switchWorkspace: (workspaceId: number) => Promise<void>;
  selectWorkspaceFromResponse: (
    workspace: Workspace,
    persistenceMode?: WorkspaceSelectionPersistenceMode,
  ) => Promise<void>;
  refreshWorkspace: () => Promise<void>;
  refreshWorkspaces: () => Promise<void>;
  userRole: WorkspaceRole | null;
  enabledFeatures: string[];
  hasFeature: (feature: string) => boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

interface WorkspaceProviderProps {
  children: ReactNode;
}

const INVITE_WORKSPACE_QUERY_PARAM = "invite_workspace_id";
const INVITE_COMPANY_QUERY_PARAM = "invite_company_id";

function getCurrentWorkspaceStorageKey(userId: string, companyId: number): string {
  return `currentWorkspaceId:${encodeURIComponent(userId)}:${companyId}`;
}

function readStoredWorkspaceID(userId: string, companyId: number): string | null {
  return typeof window !== "undefined"
    ? localStorage.getItem(getCurrentWorkspaceStorageKey(userId, companyId))
    : null;
}

interface InitialWorkspaceSelectionOptions {
  availableWorkspaces: Workspace[];
  storedWorkspaceId?: string | null;
}

interface InitialWorkspaceSelection {
  workspaceId: number | null;
  persistenceMode: WorkspaceSelectionPersistenceMode;
}

export type WorkspaceSelectionPersistenceMode = "persist" | "temporary" | "clear";

interface InviteWorkspaceResolution {
  action: "none" | "clear_query" | "switch_company" | "select_workspace";
  companyId?: number;
  workspaceId?: number;
}

export interface InviteLookupState {
  workspaceId: number;
  status: "loading" | "resolved" | "failed";
  companyId: number | null;
}

interface WorkspaceSwitchGuard {
  requestedWorkspaceID: number;
  selectedWorkspaceID: number | null;
  pendingWorkspaceID: number | null;
  hasSelectedWorkspaceError: boolean;
}

function parseWorkspaceID(rawWorkspaceID: string | null | undefined): number | null {
  if (rawWorkspaceID == null || rawWorkspaceID === "") {
    return null;
  }

  const parsedWorkspaceID = Number.parseInt(rawWorkspaceID, 10);
  if (Number.isNaN(parsedWorkspaceID)) {
    return null;
  }

  return parsedWorkspaceID;
}

export function parseInviteWorkspaceID(search: string | null | undefined): number | null {
  if (search == null || search === "") {
    return null;
  }

  const queryParams = new URLSearchParams(search);
  return parseWorkspaceID(queryParams.get(INVITE_WORKSPACE_QUERY_PARAM));
}

export function parseInviteCompanyID(search: string | null | undefined): number | null {
  if (search == null || search === "") {
    return null;
  }

  const queryParams = new URLSearchParams(search);
  return parseWorkspaceID(queryParams.get(INVITE_COMPANY_QUERY_PARAM));
}

export function selectInviteWorkspaceID(
  availableWorkspaces: Workspace[],
  inviteWorkspaceID: number | null,
): number | null {
  if (inviteWorkspaceID == null) {
    return null;
  }

  const inviteWorkspace = availableWorkspaces.find(
    (workspace) => workspace.id === inviteWorkspaceID,
  );
  return inviteWorkspace?.id ?? null;
}

export function stripInviteParamsFromSearch(search: string): string {
  const queryParams = new URLSearchParams(search);
  queryParams.delete(INVITE_WORKSPACE_QUERY_PARAM);
  queryParams.delete(INVITE_COMPANY_QUERY_PARAM);
  const nextQuery = queryParams.toString();
  return nextQuery === "" ? "" : `?${nextQuery}`;
}

export function stripInviteWorkspaceIDFromSearch(search: string): string {
  return stripInviteParamsFromSearch(search);
}

export function selectInitialWorkspaceID(
  availableWorkspaces: Workspace[],
  storedWorkspaceId?: string | null,
): number | null {
  if (availableWorkspaces.length === 0) {
    return null;
  }

  const parsedStoredWorkspaceID = parseWorkspaceID(storedWorkspaceId);
  if (parsedStoredWorkspaceID != null) {
    const savedWorkspace = availableWorkspaces.find(
      (workspace) => workspace.id === parsedStoredWorkspaceID,
    );
    if (savedWorkspace != null) {
      return savedWorkspace.id;
    }
  }

  return availableWorkspaces[0]?.id ?? null;
}

export function mergeWorkspaceIntoList(
  availableWorkspaces: Workspace[],
  workspace: Workspace,
): Workspace[] {
  return [
    workspace,
    ...availableWorkspaces.filter((currentWorkspace) => currentWorkspace.id !== workspace.id),
  ];
}

export function resolveInitialWorkspaceSelection(
  options: InitialWorkspaceSelectionOptions,
): InitialWorkspaceSelection {
  return {
    workspaceId: selectInitialWorkspaceID(options.availableWorkspaces, options.storedWorkspaceId),
    persistenceMode: "persist",
  };
}

export function resolveInviteWorkspaceSelection(
  availableWorkspaces: Workspace[],
  selectedCompanyId: number | null,
  inviteCompanyId: number | null,
  inviteWorkspaceId: number | null,
): InviteWorkspaceResolution {
  if (inviteCompanyId == null && inviteWorkspaceId == null) {
    return { action: "none" };
  }

  if (inviteCompanyId != null && selectedCompanyId !== inviteCompanyId) {
    return {
      action: "switch_company",
      companyId: inviteCompanyId,
    };
  }

  if (inviteWorkspaceId == null) {
    return { action: "clear_query" };
  }

  const inviteWorkspace = availableWorkspaces.find(
    (workspace) => workspace.id === inviteWorkspaceId,
  );
  if (inviteWorkspace == null) {
    return { action: "clear_query" };
  }

  return {
    action: "select_workspace",
    workspaceId: inviteWorkspace.id,
  };
}

export function shouldSkipWorkspaceSwitch({
  requestedWorkspaceID,
  selectedWorkspaceID,
  pendingWorkspaceID,
  hasSelectedWorkspaceError,
}: WorkspaceSwitchGuard): boolean {
  const isRetryForFailedSelection =
    selectedWorkspaceID === requestedWorkspaceID && hasSelectedWorkspaceError;
  if (isRetryForFailedSelection) {
    return false;
  }

  return (
    selectedWorkspaceID === requestedWorkspaceID || pendingWorkspaceID === requestedWorkspaceID
  );
}

/**
 * Progress of the lookup that finds an invite workspace's company when the invite link carries
 * only a workspace ID. "none" means no lookup applies.
 */
export type InviteLookupProgress = "none" | "pending" | "failed" | "resolved";

export function getInviteLookupProgress(
  inviteCompanyID: number | null,
  inviteWorkspaceID: number | null,
  inviteLookup: InviteLookupState | null,
): InviteLookupProgress {
  if (inviteCompanyID != null || inviteWorkspaceID == null) {
    return "none";
  }
  if (
    inviteLookup == null ||
    inviteLookup.workspaceId !== inviteWorkspaceID ||
    inviteLookup.status === "loading"
  ) {
    return "pending";
  }
  return inviteLookup.status === "failed" ? "failed" : "resolved";
}

export interface WorkspaceSelectionState {
  userId: string | null;
  authLoading: boolean;
  companiesLoading: boolean;
  companyTransitioning: boolean;
  selectedCompanyId: number | null;
  activeCompanyID: number | null;
  availableWorkspaces: Workspace[];
  workspacesLoading: boolean;
  hasWorkspacesError: boolean;
  selectedWorkspaceID: number | null;
  currentWorkspaceID: number | null;
  hasInitializedSelection: boolean;
  inviteLookupProgress: InviteLookupProgress;
  resolvedInviteCompanyID: number | null;
  inviteWorkspaceID: number | null;
  pendingSwitchCompanyID: number | null;
}

export type WorkspaceSelectionStep =
  | { kind: "wait" }
  | { kind: "switch_company_temporarily"; companyId: number }
  | { kind: "select_invite_workspace"; workspaceId: number; alreadySelected: boolean }
  | { kind: "keep_selection" }
  | { kind: "resolve_initial"; userId: string; companyId: number }
  | { kind: "clear_empty"; companyId: number };

export interface WorkspaceSelectionPlan {
  clearInviteQuery: boolean;
  step: WorkspaceSelectionStep;
}

const WAIT: WorkspaceSelectionStep = { kind: "wait" };

// Invite-driven steps take precedence over normal resolution. Null means no invite step applies.
function planInviteStep(
  state: WorkspaceSelectionState,
  invite: InviteWorkspaceResolution,
): WorkspaceSelectionStep | null {
  if (invite.action === "switch_company" && invite.companyId != null) {
    return state.pendingSwitchCompanyID === invite.companyId
      ? WAIT
      : { kind: "switch_company_temporarily", companyId: invite.companyId };
  }

  if (state.resolvedInviteCompanyID != null && state.workspacesLoading) {
    return WAIT;
  }

  if (invite.action === "select_workspace" && invite.workspaceId != null) {
    return {
      kind: "select_invite_workspace",
      workspaceId: invite.workspaceId,
      alreadySelected:
        state.selectedWorkspaceID === invite.workspaceId &&
        state.currentWorkspaceID === invite.workspaceId,
    };
  }

  return null;
}

function planDefaultStep(
  state: WorkspaceSelectionState,
  userId: string,
  companyId: number,
): WorkspaceSelectionStep {
  const { availableWorkspaces, selectedWorkspaceID } = state;
  if (
    selectedWorkspaceID != null &&
    availableWorkspaces.some((workspace) => workspace.id === selectedWorkspaceID)
  ) {
    return { kind: "keep_selection" };
  }

  // An empty company initializes with no selection. Resolve again when its list later gains a
  // workspace, for example after an admin membership change provisions one. A stale non-null
  // selection is left to the fallback effect.
  if (state.hasInitializedSelection && selectedWorkspaceID != null) {
    return WAIT;
  }

  if (availableWorkspaces.length > 0) {
    return { kind: "resolve_initial", userId, companyId };
  }

  if (!state.workspacesLoading && !state.hasWorkspacesError) {
    return { kind: "clear_empty", companyId };
  }

  return WAIT;
}

/**
 * Decides the next workspace-selection step for the current company. Pure, so the provider's
 * effect only applies the result.
 */
export function planWorkspaceSelection(state: WorkspaceSelectionState): WorkspaceSelectionPlan {
  const { userId, activeCompanyID } = state;
  if (
    userId == null ||
    state.authLoading ||
    state.companiesLoading ||
    state.companyTransitioning ||
    state.selectedCompanyId == null ||
    activeCompanyID == null ||
    state.inviteLookupProgress === "pending"
  ) {
    return { clearInviteQuery: false, step: WAIT };
  }

  const inviteLookupFailed = state.inviteLookupProgress === "failed";
  const invite = resolveInviteWorkspaceSelection(
    state.availableWorkspaces,
    state.selectedCompanyId,
    state.resolvedInviteCompanyID,
    state.inviteWorkspaceID,
  );
  const inviteStep = planInviteStep(state, invite);
  if (inviteStep != null) {
    return {
      clearInviteQuery: inviteLookupFailed || inviteStep.kind === "select_invite_workspace",
      step: inviteStep,
    };
  }

  return {
    clearInviteQuery: inviteLookupFailed || invite.action === "clear_query",
    step: planDefaultStep(state, userId, activeCompanyID),
  };
}

export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId, isLoading: authLoading } = useAuth();
  const { selectedCompanyId, companiesLoading, switchCompanyTemporarily } = useCompany();
  const [selectedWorkspaceID, setSelectedWorkspaceID] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const [pendingSwitchCompanyID, setPendingSwitchCompanyID] = useState<number | null>(null);
  const [pendingSwitchWorkspaceID, setPendingSwitchWorkspaceID] = useState<number | null>(null);
  const [workspaceCompanyID, setWorkspaceCompanyID] = useState<number | null>(selectedCompanyId);
  const [inviteLookup, setInviteLookup] = useState<InviteLookupState | null>(null);
  const previousUserIdRef = useRef<string | null>(null);

  const {
    useWorkspaces,
    useWorkspaceContext: useWorkspaceContextHook,
    getWorkspace,
    getCompanyWorkspacesKey,
    getWorkspaceContextKey,
  } = useWorkspaceApi();

  const companyTransitioning = workspaceCompanyID !== selectedCompanyId;
  const activeCompanyID = companyTransitioning ? null : workspaceCompanyID;

  const {
    data: availableWorkspaces = [],
    error: workspacesError,
    isLoading: workspacesLoading,
    mutate: mutateWorkspaces,
  } = useWorkspaces(
    activeCompanyID,
    userId != null && authLoading === false && activeCompanyID != null,
  );

  const activeSelectedWorkspaceID =
    activeCompanyID != null &&
    availableWorkspaces.some((workspace) => workspace.id === selectedWorkspaceID)
      ? selectedWorkspaceID
      : null;

  const {
    data: workspaceContextData,
    error: workspaceContextError,
    isLoading: workspaceContextLoading,
    mutate: mutateWorkspace,
  } = useWorkspaceContextHook(activeSelectedWorkspaceID);

  const currentWorkspace =
    activeCompanyID != null &&
    activeSelectedWorkspaceID != null &&
    workspaceContextData?.workspace.company_id === activeCompanyID
      ? workspaceContextData
      : null;
  const selectedWorkspace =
    activeCompanyID != null
      ? (availableWorkspaces.find((workspace) => workspace.id === selectedWorkspaceID) ?? null)
      : null;

  const inviteWorkspaceID = parseInviteWorkspaceID(location.search);
  const inviteCompanyID = parseInviteCompanyID(location.search);
  const resolvedInviteCompanyID =
    inviteCompanyID ??
    (inviteLookup?.workspaceId === inviteWorkspaceID && inviteLookup.status === "resolved"
      ? inviteLookup.companyId
      : null);
  const inviteLookupProgress = getInviteLookupProgress(
    inviteCompanyID,
    inviteWorkspaceID,
    inviteLookup,
  );

  const clearStoredWorkspaceSelection = useCallback(
    (companyId: number | null) => {
      if (typeof window === "undefined" || userId == null || companyId == null) {
        return;
      }

      localStorage.removeItem(getCurrentWorkspaceStorageKey(userId, companyId));
    },
    [userId],
  );

  const clearInviteParams = useCallback(() => {
    const nextSearch = stripInviteParamsFromSearch(location.search);
    if (nextSearch === location.search) {
      return;
    }

    void navigate(`${location.pathname}${nextSearch}${location.hash}`, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);

  useLayoutEffect(() => {
    if (workspaceCompanyID === selectedCompanyId) {
      return;
    }

    setHasInitializedSelection(false);
    setPendingSwitchCompanyID(null);
    setPendingSwitchWorkspaceID(null);
    setSelectedWorkspaceID(null);
    setLoading(false);
    setWorkspaceCompanyID(selectedCompanyId);
  }, [selectedCompanyId, workspaceCompanyID]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (previousUserIdRef.current === userId) {
      return;
    }

    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId != null && previousUserId !== userId) {
      void mutate((key) => isCompanyWorkspaceCacheKey(key, previousUserId), undefined, false);
    }

    setHasInitializedSelection(false);
    setPendingSwitchCompanyID(null);
    setPendingSwitchWorkspaceID(null);
    setSelectedWorkspaceID(null);
    setLoading(false);
    setWorkspaceCompanyID(null);
    setInviteLookup(null);
    void mutateWorkspaces([], false);
  }, [authLoading, mutateWorkspaces, userId]);

  useEffect(() => {
    if (inviteCompanyID != null || inviteWorkspaceID == null || userId == null || authLoading) {
      setInviteLookup(null);
      return;
    }

    if (
      inviteLookup != null &&
      inviteLookup.workspaceId === inviteWorkspaceID &&
      (inviteLookup.status === "loading" || inviteLookup.status === "resolved")
    ) {
      return;
    }

    let cancelled = false;
    setInviteLookup({
      workspaceId: inviteWorkspaceID,
      status: "loading",
      companyId: null,
    });

    void getWorkspace(inviteWorkspaceID)
      .then((workspace) => {
        if (cancelled) {
          return;
        }

        setInviteLookup({
          workspaceId: inviteWorkspaceID,
          status: "resolved",
          companyId: workspace.company_id,
        });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setInviteLookup({
          workspaceId: inviteWorkspaceID,
          status: "failed",
          companyId: null,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, getWorkspace, inviteCompanyID, inviteLookup, inviteWorkspaceID, userId]);

  const refreshWorkspaces = useCallback(async (): Promise<void> => {
    if (userId == null || authLoading || activeCompanyID == null) {
      return;
    }

    await mutateWorkspaces();
  }, [activeCompanyID, authLoading, mutateWorkspaces, userId]);

  const switchWorkspaceInternal = useCallback(
    async (
      workspaceId: number,
      persistenceMode: WorkspaceSelectionPersistenceMode,
    ): Promise<void> => {
      const availableWorkspace = availableWorkspaces.find(
        (workspace) => workspace.id === workspaceId,
      );
      if (availableWorkspace == null) {
        throw new Error("Selected workspace is not available in the active company");
      }

      const hasSelectedWorkspaceError =
        selectedWorkspaceID === workspaceId && workspaceContextError != null;
      const shouldSkip = shouldSkipWorkspaceSwitch({
        requestedWorkspaceID: workspaceId,
        selectedWorkspaceID,
        pendingWorkspaceID: pendingSwitchWorkspaceID,
        hasSelectedWorkspaceError,
      });

      if (shouldSkip) {
        return;
      }

      setPendingSwitchWorkspaceID(workspaceId);
      setLoading(true);
      if (selectedWorkspaceID === workspaceId) {
        void mutateWorkspace();
      } else {
        setSelectedWorkspaceID(workspaceId);
      }

      if (typeof window !== "undefined") {
        if (persistenceMode === "persist") {
          if (userId != null && activeCompanyID != null) {
            localStorage.setItem(
              getCurrentWorkspaceStorageKey(userId, activeCompanyID),
              workspaceId.toString(),
            );
          }
        }
        if (persistenceMode === "clear") {
          clearStoredWorkspaceSelection(activeCompanyID);
        }
      }
    },
    [
      activeCompanyID,
      availableWorkspaces,
      clearStoredWorkspaceSelection,
      mutateWorkspace,
      pendingSwitchWorkspaceID,
      selectedWorkspaceID,
      userId,
      workspaceContextError,
    ],
  );

  const switchWorkspace = useCallback(
    async (workspaceId: number): Promise<void> => {
      await switchWorkspaceInternal(workspaceId, "persist");
    },
    [switchWorkspaceInternal],
  );

  const selectWorkspaceFromResponse = useCallback(
    async (
      workspace: Workspace,
      persistenceMode: WorkspaceSelectionPersistenceMode = "persist",
    ): Promise<void> => {
      if (activeCompanyID == null) {
        throw new Error("No active company selected");
      }
      if (workspace.company_id !== activeCompanyID) {
        throw new Error("Selected workspace is not available in the active company");
      }

      const workspacesKey = getCompanyWorkspacesKey(activeCompanyID);
      if (workspacesKey != null) {
        await mutate(
          workspacesKey,
          (current: Workspace[] | undefined) =>
            mergeWorkspaceIntoList(Array.isArray(current) ? current : [], workspace),
          false,
        );
        void mutate(workspacesKey);
      }

      setPendingSwitchWorkspaceID(workspace.id);
      setLoading(true);
      if (selectedWorkspaceID === workspace.id) {
        void mutateWorkspace();
      } else {
        setSelectedWorkspaceID(workspace.id);
      }

      if (typeof window !== "undefined") {
        if (persistenceMode === "persist") {
          if (userId != null) {
            localStorage.setItem(
              getCurrentWorkspaceStorageKey(userId, activeCompanyID),
              workspace.id.toString(),
            );
          }
        }
        if (persistenceMode === "clear") {
          clearStoredWorkspaceSelection(activeCompanyID);
        }
      }
    },
    [
      activeCompanyID,
      clearStoredWorkspaceSelection,
      getCompanyWorkspacesKey,
      mutateWorkspace,
      selectedWorkspaceID,
      userId,
    ],
  );

  const refreshWorkspace = useCallback(async (): Promise<void> => {
    const workspaceIDToRefresh = currentWorkspace?.workspace.id ?? activeSelectedWorkspaceID;
    if (workspaceIDToRefresh == null) {
      return;
    }

    if (workspaceIDToRefresh === activeSelectedWorkspaceID) {
      await mutateWorkspace();
      return;
    }

    const workspaceContextKey = getWorkspaceContextKey(workspaceIDToRefresh);
    if (workspaceContextKey != null) {
      await mutate(workspaceContextKey);
    }
  }, [
    activeSelectedWorkspaceID,
    currentWorkspace?.workspace.id,
    getWorkspaceContextKey,
    mutateWorkspace,
  ]);

  useEffect(() => {
    const { clearInviteQuery, step } = planWorkspaceSelection({
      userId,
      authLoading,
      companiesLoading,
      companyTransitioning,
      selectedCompanyId,
      activeCompanyID,
      availableWorkspaces,
      workspacesLoading,
      hasWorkspacesError: workspacesError != null,
      selectedWorkspaceID,
      currentWorkspaceID: currentWorkspace?.workspace.id ?? null,
      hasInitializedSelection,
      inviteLookupProgress,
      resolvedInviteCompanyID,
      inviteWorkspaceID,
      pendingSwitchCompanyID,
    });

    if (clearInviteQuery) {
      clearInviteParams();
    }

    switch (step.kind) {
      case "wait":
        return;
      case "switch_company_temporarily":
        setPendingSwitchCompanyID(step.companyId);
        void switchCompanyTemporarily(step.companyId)
          .catch((error: unknown) => {
            console.error("Failed to switch to the invited company:", error);
            clearInviteParams();
          })
          .finally(() => {
            setPendingSwitchCompanyID((current) => (current === step.companyId ? null : current));
          });
        return;
      case "select_invite_workspace":
        setHasInitializedSelection(true);
        if (!step.alreadySelected) {
          void switchWorkspaceInternal(step.workspaceId, "temporary");
        }
        return;
      case "keep_selection":
        setHasInitializedSelection(true);
        return;
      case "resolve_initial": {
        const initialSelection = resolveInitialWorkspaceSelection({
          availableWorkspaces,
          storedWorkspaceId: readStoredWorkspaceID(step.userId, step.companyId),
        });
        if (initialSelection.workspaceId == null) {
          return;
        }

        setHasInitializedSelection(true);
        void switchWorkspaceInternal(
          initialSelection.workspaceId,
          initialSelection.persistenceMode,
        );
        return;
      }
      case "clear_empty":
        setHasInitializedSelection(true);
        setSelectedWorkspaceID(null);
        setLoading(false);
        clearStoredWorkspaceSelection(step.companyId);
        return;
    }
  }, [
    activeCompanyID,
    authLoading,
    availableWorkspaces,
    clearInviteParams,
    companiesLoading,
    companyTransitioning,
    currentWorkspace?.workspace.id,
    hasInitializedSelection,
    inviteLookupProgress,
    inviteWorkspaceID,
    pendingSwitchCompanyID,
    resolvedInviteCompanyID,
    selectedCompanyId,
    selectedWorkspaceID,
    switchCompanyTemporarily,
    switchWorkspaceInternal,
    clearStoredWorkspaceSelection,
    userId,
    workspacesError,
    workspacesLoading,
  ]);

  useEffect(() => {
    const pendingWorkspaceID = pendingSwitchWorkspaceID;
    if (pendingWorkspaceID == null) {
      return;
    }

    if (selectedWorkspaceID == null || selectedWorkspaceID !== pendingWorkspaceID) {
      setPendingSwitchWorkspaceID(null);
      setLoading(false);
      return;
    }

    if (activeSelectedWorkspaceID == null || workspaceContextLoading === true) {
      return;
    }

    setPendingSwitchWorkspaceID(null);
    setLoading(false);
  }, [
    activeSelectedWorkspaceID,
    pendingSwitchWorkspaceID,
    selectedWorkspaceID,
    workspaceContextError,
    workspaceContextLoading,
  ]);

  useEffect(() => {
    if (companyTransitioning || workspacesLoading === true) {
      return;
    }

    if (activeCompanyID == null) {
      return;
    }

    if (availableWorkspaces.length === 0) {
      if (selectedWorkspaceID != null) {
        setSelectedWorkspaceID(null);
      }
      setPendingSwitchWorkspaceID(null);
      setLoading(false);
      clearStoredWorkspaceSelection(activeCompanyID);
      return;
    }

    if (selectedWorkspaceID == null) {
      return;
    }

    const selectedExists = availableWorkspaces.some(
      (workspace) => workspace.id === selectedWorkspaceID,
    );
    if (selectedExists) {
      return;
    }

    const fallbackWorkspaceID = selectInitialWorkspaceID(
      availableWorkspaces,
      typeof window !== "undefined" && userId != null
        ? localStorage.getItem(getCurrentWorkspaceStorageKey(userId, activeCompanyID))
        : null,
    );
    if (fallbackWorkspaceID == null) {
      setSelectedWorkspaceID(null);
      clearStoredWorkspaceSelection(activeCompanyID);
      return;
    }

    void switchWorkspace(fallbackWorkspaceID);
  }, [
    activeCompanyID,
    availableWorkspaces,
    clearStoredWorkspaceSelection,
    companyTransitioning,
    selectedWorkspaceID,
    switchWorkspace,
    userId,
    workspacesLoading,
  ]);

  const userRole = currentWorkspace?.role ?? null;
  const enabledFeatures = currentWorkspace?.enabled_features ?? [];

  const value: WorkspaceContextType = {
    currentWorkspace,
    selectedWorkspace,
    availableWorkspaces,
    loading: loading || companyTransitioning || workspaceContextLoading === true,
    workspacesLoading: companyTransitioning || workspacesLoading === true,
    workspacesError: workspacesError ?? null,
    workspaceContextError: workspaceContextError ?? null,
    switchWorkspace,
    selectWorkspaceFromResponse,
    refreshWorkspace,
    refreshWorkspaces,
    userRole,
    enabledFeatures,
    hasFeature: (feature: string): boolean => hasFeature(enabledFeatures, feature),
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextType {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
}
